/**
 * Aus HTML lesbaren Klartext machen — für **beide** Gleise.
 *
 * Prüfungstexte kommen als HTML herein: aus dem Classic-Bestand als ganze Stationsseiten, aus
 * Relay als Vignette und Fragetext des Editors. Der Bogen zeigt aber Text, keine Seite.
 *
 * ⚠️ **Die Entitäten sind nicht nur die bekannten fünf.** Am echten Classic-Server (19
 * Prüfungen, am 2026-09-11 nachgesehen) stecken in nahezu jedem Aufgabentext `&#xa0;` und
 * `&#8203;` — ein geschütztes und ein Nullbreiten-Leerzeichen, beide numerisch kodiert. Wer nur
 * `&nbsp;` und Freunde ersetzt, zeigt dem Prüfer `1.1&#xa0;Was müssen Sie …`.
 *
 * Nullbreitenzeichen fallen ganz weg: sie sind unsichtbar, zählen aber bei jeder Längen- und
 * Leerprüfung mit — ein Antworttext aus `<p>&#8203;</p>` sähe sonst gefüllt aus und wäre leer.
 *
 * ⚠️ **Hier steht nichts Protokollspezifisches.** Was nur ein Gleis betrifft — etwa Classics
 * `javascript:`-Link mit den Prüferinstruktionen — gibt der Aufrufer als `entferne` mit. Sonst
 * stünde eine Eigenheit des Classic-Servers im gemeinsamen Teil, und genau das soll nicht sein
 * (`memory/zweigleisig.md`).
 */

export interface TextOptionen {
  /** Mit Absätzen statt allem in einer Zeile. */
  mitAbsaetzen?: boolean;
  /** Was **vor** dem Strippen verschwinden soll — Eigenheiten des jeweiligen Servers. */
  entferne?: readonly RegExp[];
}

/**
 * Das Zeichen zu einer Nummer — oder die Entität unverändert, wenn es die Nummer nicht gibt.
 * `String.fromCodePoint` wirft über 0x10FFFF, und ein einziges `&#9999999;` in einem
 * Aufgabennamen machte sonst die ganze Prüfung unlesbar (Audit 2026-10-11).
 */
function zeichen(nummer: number, roh: string): string {
  return nummer <= 0x10ffff ? String.fromCodePoint(nummer) : roh;
}

export function htmlZuText(html: string, optionen: TextOptionen = {}): string {
  const mitAbsaetzen = optionen.mitAbsaetzen ?? false;
  const trenner = mitAbsaetzen ? '\n' : ' ';

  let roh = html;
  for (const muster of optionen.entferne ?? []) roh = roh.replace(muster, '');

  const text = roh
    .replace(/<br\s*\/?>/gi, trenner)
    // `<li>` setzt den Umbruch selbst, deshalb steht `li` unten **nicht** mit im schliessenden
    // Satz — sonst stünde zwischen zwei Aufzählungspunkten eine Leerzeile.
    .replace(/<li\b[^>]*>/gi, mitAbsaetzen ? '\n• ' : ' ')
    .replace(/<\/(p|div|tr|h[1-6])>/gi, trenner)
    .replace(/<[^>]*>/g, '')
    .replace(/&#x([0-9a-f]+);/gi, (roh, hex: string) => zeichen(parseInt(hex, 16), roh))
    .replace(/&#(\d+);/g, (roh, dez: string) => zeichen(Number(dez), roh))
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    // `&amp;` zuletzt, sonst würde aus `&amp;lt;` erst `&lt;` und dann `<`.
    .replace(/&amp;/gi, '&')
    // Nullbreiten- und geschützte Leerzeichen, nachdem die Entitäten aufgelöst sind.
    .replace(/[​‌‍﻿]/g, '')
    .replace(/ /g, ' ');

  if (!mitAbsaetzen) return text.replace(/\s+/g, ' ').trim();

  return text
    // Waagerechten Leerraum zusammenziehen, aber die Umbrüche stehen lassen.
    .replace(/[^\S\n]+/g, ' ')
    .replace(/ ?\n ?/g, '\n')
    // Mehr als eine Leerzeile sagt nichts mehr; der Bestand ist voll von `<p>&#xa0;</p>`.
    .replace(/\n{3,}/g, '\n\n')
    .split('\n')
    .map((zeile) => zeile.trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Dasselbe, aber gekürzt — für Kopfzeilen und Popups. */
export function htmlZuKurztext(html: string, hoechstens = 120, optionen: TextOptionen = {}): string {
  const text = htmlZuText(html, { ...optionen, mitAbsaetzen: false });
  return text.length > hoechstens ? text.slice(0, hoechstens - 1).trimEnd() + '…' : text;
}
